import React, { useEffect, useState } from "react";
import { Navigate, RouteProps, useNavigate } from "react-router-dom";
import { getLoggedInUserDetails } from "../services/Apis";
import { saveToStorage } from "../services/storage";
import { useUserContext } from "../context/UserContext";
import { LoadingOutlined } from '@ant-design/icons';

/**
 * ProtectedRoute Component
 * 
 * This component acts as a wrapper for routes that require authentication.
 * It checks if the user is logged in by verifying the presence of a login token.
 * If not authenticated, it redirects to the login page.
 * It also handles fetching and storing user details if they don't exist locally.
 */
const ProtectedRoute: React.FC<RouteProps> = ({ children }) => {
    const navigate = useNavigate(); // React Router hook for navigation
    const { setUserContext } = useUserContext(); // Access user context for global state
    const [isLoading, setIsLoading] = useState(true); // Loading state to show spinner during authentication

    useEffect(() => {
        /**
         * Fetches user details from the API using the stored token
         * Updates user context with the retrieved data
         * Handles authentication errors by redirecting to auth-error page
         */
        const fetchToken = async () => {
            const userDetails: any = await getLoggedInUserDetails();
            if (userDetails.error_status === 401) {
                // Token is invalid or expired, clear it and redirect to error page
                localStorage.removeItem("login_token");
                navigate("/auth-error");
            } else {
                saveToStorage("user_details", JSON.stringify(userDetails.data)); // Save user details to local storage
                setUserContext(userDetails.data);
            }
            setIsLoading(false); // Mark loading as complete regardless of outcome
        };

        // Check if login token exists in local storage
        const loginToken = localStorage.getItem("login_token");
        if (!loginToken) {
            // No token found, redirect to login page
            navigate("/login");
            return;
        }

        // Check if user details are already cached in local storage
        const userDetails = localStorage.getItem("user_details");
        if (!userDetails) {
            // User details not found, fetch them from API
            fetchToken();
        } else {
            // User details exist locally, no need for API call
            setIsLoading(false); // If user details exist, no need to fetch
        }
    }, []); // Empty dependency array ensures this effect runs only once on component mount

    if (isLoading) {
        // Render a loading spinner while authentication is in progress
        return <div><LoadingOutlined style={{ fontSize: "40px", color: "cornflowerblue" }} /></div>;
    }

    // Render children components when authentication is complete
    return <>{children}</>;
};

export default ProtectedRoute;